import {
  HStack,
  MixpanelEvent,
  MixpanelEventProperty,
  SELECTION_LIST_TALLER_ITEM_HEIGHT,
  SelectionsList,
  useMarketsContext,
  useMixpanel,
  type SelectionListItem,
  type SelectionListSortProperty,
  type SortState,
} from '@talos/kyoko';
import { Fragment, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useMarketSelectorContext } from './MarketSelectorContext';
import { AvailabilityEnum, type MarketSelectorSelectionListContribution } from './types';
import { isMarketSelectorItemSelectable, marketSelectorItemComparator } from './utils/items';

interface MarketSelectorSelectionsListProps {
  placeholder?: string;
}

export const MarketSelectorSelectionsList = ({ placeholder }: MarketSelectorSelectionsListProps) => {
  const [sort, setSort] = useState<SortState<SelectionListSortProperty> | undefined>({
    key: 'label',
    direction: '-',
  });
  const {
    selectionsSet,
    items,
    itemsMap,
    unselect,
    enrichments,
    extensions,
    selections,
    selectionListShowAll,
    setSelectionListShowAll,
    selectMarket,
    select,
    selectOnly,
    unselectAll,
    selectAll,
  } = useMarketSelectorContext();
  const { marketDisplayNameByName } = useMarketsContext();
  const mixpanel = useMixpanel();

  const selectionListEnrichmentSpecs = useMemo(
    () =>
      [...enrichments, ...extensions].map(spec => spec.selectionList).filter(selectionList => selectionList != null),
    [enrichments, extensions]
  );
  const selectionListHeaderEnrichmentSpec = useMemo(
    () => getSingletonSelectionListContribution(selectionListEnrichmentSpecs, 'selectionListHeaderSuffix'),
    [selectionListEnrichmentSpecs]
  );
  const selectionListComparatorEnrichmentSpec = useMemo(
    () => getSingletonSelectionListContribution(selectionListEnrichmentSpecs, 'comparator'),
    [selectionListEnrichmentSpecs]
  );
  const selectionListParentComparatorEnrichmentSpec = useMemo(
    () => getSingletonSelectionListContribution(selectionListEnrichmentSpecs, 'parentComparator'),
    [selectionListEnrichmentSpecs]
  );

  const getSelectionListHeaderSuffix = useCallback(
    () => selectionListHeaderEnrichmentSpec?.selectionListHeaderSuffix?.(selections),
    [selectionListHeaderEnrichmentSpec, selections]
  );

  const selectionListComparator = useMemo(() => {
    if (sort == null) {
      return () => 0; // shouldnt happen, but need to catch it
    }

    const baseComparator =
      sort.key === 'label' ? marketSelectorItemComparator : selectionListComparatorEnrichmentSpec?.comparator;
    const comparator = (a: SelectionListItem, b: SelectionListItem) => {
      if (!baseComparator) {
        return 0;
      }

      return baseComparator(itemsMap.get(a.key), itemsMap.get(b.key), sort.direction);
    };
    return comparator;
  }, [sort, itemsMap, selectionListComparatorEnrichmentSpec?.comparator]);

  const enrichmentParentComparator = selectionListParentComparatorEnrichmentSpec?.parentComparator;
  const selectionListParentComparator = useMemo(() => {
    if (sort == null) {
      return () => 0; // shouldnt happen, but need to catch it
    }

    const labelComparator = (a: SelectionListItem, b: SelectionListItem) => {
      if (sort.direction === '+') {
        // Instead of reversing the resulting array, flip the pointers
        const temp = a;
        a = b;
        b = temp;
      }

      return a.label.localeCompare(b.label) || a.key.localeCompare(b.key);
    };

    const suffixComparator = (a: SelectionListItem, b: SelectionListItem) => {
      return enrichmentParentComparator ? enrichmentParentComparator(a.key, b.key, sort.direction) : 0;
    };

    return sort.key === 'suffix' ? suffixComparator : labelComparator;
  }, [sort, enrichmentParentComparator]);

  const selectionListItemSuffix = useCallback(
    (itemName: string, selected: boolean) => {
      const suffixes = selectionListEnrichmentSpecs
        .map(spec => spec.selectionListItemSuffix?.(itemName, selected))
        .filter(suffix => suffix != null);
      if (!suffixes.length) {
        return undefined;
      }

      return (
        <SelectionListItemSuffix gap="spacing4" justifyContent="flex-end">
          {suffixes.map((suffix, index) => (
            <Fragment key={index}>{suffix}</Fragment>
          ))}
        </SelectionListItemSuffix>
      );
    },
    [selectionListEnrichmentSpecs]
  );

  const selectionListItemLabelSuffix = useCallback(
    (itemName: string, selected: boolean) => {
      const suffixes = selectionListEnrichmentSpecs
        .map(spec => spec.selectionListItemLabelSuffix?.(itemName, selected))
        .filter(suffix => suffix != null);
      if (!suffixes.length) {
        return undefined;
      }

      return (
        <SelectionListItemLabelSuffix data-testid="selection-list-item-label-suffix" gap="spacing4">
          {suffixes.map((suffix, index) => (
            <Fragment key={index}>{suffix}</Fragment>
          ))}
        </SelectionListItemLabelSuffix>
      );
    },
    [selectionListEnrichmentSpecs]
  );

  const selectionListItems = useMemo(() => {
    return items
      .filter(item => selectionsSet.has(item.name) || isMarketSelectorItemSelectable(item))
      .map(item => {
        const selected = selectionsSet.has(item.name);

        return {
          selected,
          disabled: selected ? false : !isMarketSelectorItemSelectable(item),
          key: item.name,
          label: item.displayName,
          parentKey: item.market.Name,
          labelSuffix: selectionListItemLabelSuffix(item.name, selected),
          warning: item.availability.availability < AvailabilityEnum.Ok ? item.availability?.render?.() : undefined,
          suffix: selectionListItemSuffix(item.name, selected),
        } satisfies SelectionListItem;
      });
  }, [items, selectionsSet, selectionListItemLabelSuffix, selectionListItemSuffix]);

  const getParentLabel = useCallback(
    (marketName: string) => {
      return marketDisplayNameByName.get(marketName) ?? marketName;
    },
    [marketDisplayNameByName]
  );

  const handleDelete = useCallback(
    (selection: string) => {
      unselect(selection);
      mixpanel.track(MixpanelEvent.UnselectMarketAccount);
    },
    [unselect, mixpanel]
  );

  const handleOnly = useCallback(
    (key: string) => {
      mixpanel.track(MixpanelEvent.UpdateMarketSelection, {
        [MixpanelEventProperty.Method]: 'Only',
      });
      selectOnly(key);
    },
    [mixpanel, selectOnly]
  );

  const handleShowAllChange = useCallback(
    (value: boolean) => {
      mixpanel.track(value ? MixpanelEvent.ClickShowAllMarketAccounts : MixpanelEvent.ClickShowSelectedMarketAccounts);
      setSelectionListShowAll(value);
    },
    [mixpanel, setSelectionListShowAll]
  );

  return (
    <SelectionsList
      items={selectionListItems}
      placeholder={placeholder}
      onDelete={handleDelete}
      getHeaderSuffix={getSelectionListHeaderSuffix}
      getParentLabel={getParentLabel}
      itemHeight={
        selectionListEnrichmentSpecs.some(spec => spec.selectionListItemHeight === 'taller')
          ? SELECTION_LIST_TALLER_ITEM_HEIGHT
          : undefined
      }
      sort={sort}
      sortComparator={selectionListComparator}
      parentSortComparator={selectionListParentComparator}
      onSortChange={setSort}
      onOnlyClicked={handleOnly}
      onDeleteAll={unselectAll}
      onSelectAll={selectAll}
      showAll={selectionListShowAll}
      onShowAllChange={handleShowAllChange}
      onSelect={select}
      onSelectParent={selectMarket}
    />
  );
};

const SelectionListItemSuffix = styled(HStack)`
  min-width: 0;
`;

const SelectionListItemLabelSuffix = styled(HStack)`
  min-width: 0;
`;

function getSingletonSelectionListContribution<T extends keyof MarketSelectorSelectionListContribution>(
  contributions: MarketSelectorSelectionListContribution[],
  property: T
) {
  const matches = contributions.filter(contribution => contribution[property] != null);
  if (import.meta.env.DEV && matches.length > 1) {
    throw new Error(`Multiple Market Selector contributions define ${property}`);
  }
  return matches[0];
}
